import { ARIZONA_MOCK_BOUNDS } from "./config.js";

// These anchors deliberately sit inside the supplied Arizona county geometry.
// The same weighted, bounded generator is shared by every POC mock category so
// overlay points remain geographically comparable to the human report cells.
export const ARIZONA_REPORT_ANCHORS = Object.freeze([
  { latitude: 33.4484, longitude: -112.074, weight: 31 }, // Phoenix
  { latitude: 32.2226, longitude: -110.9747, weight: 22 }, // Tucson
  { latitude: 35.1983, longitude: -111.6513, weight: 10 }, // Flagstaff
  { latitude: 33.4942, longitude: -111.9261, weight: 12 }, // Scottsdale
  { latitude: 33.3528, longitude: -111.789, weight: 10 }, // Mesa
  { latitude: 32.6927, longitude: -114.25, latitudeJitter: 0.08, longitudeJitter: 0.08, weight: 7 }, // Yuma
  { latitude: 34.54, longitude: -112.4685, weight: 5 }, // Prescott
  { latitude: 35.0242, longitude: -110.6974, weight: 3 }, // Show Low
]);

const ANCHOR_WEIGHT_TOTAL = ARIZONA_REPORT_ANCHORS.reduce((total, anchor) => total + anchor.weight, 0);

export function createArizonaMockCoordinate(random) {
  if (typeof random !== "function") throw new TypeError("Expected a deterministic random function for Arizona mock coordinates.");
  const anchor = selectAnchor(random());
  const latitude = clamp(
    anchor.latitude + centeredJitter(random) * (anchor.latitudeJitter ?? 0.22),
    ARIZONA_MOCK_BOUNDS.minimumLatitude,
    ARIZONA_MOCK_BOUNDS.maximumLatitude,
  );
  const longitude = clamp(
    anchor.longitude + centeredJitter(random) * (anchor.longitudeJitter ?? 0.26),
    ARIZONA_MOCK_BOUNDS.minimumLongitude,
    ARIZONA_MOCK_BOUNDS.maximumLongitude,
  );
  return Object.freeze({
    latitude: Number(latitude.toFixed(6)),
    longitude: Number(longitude.toFixed(6)),
  });
}

export function createSeededMockRandom(seed) {
  let value = Number(seed) >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let output = value;
    output = Math.imul(output ^ (output >>> 15), output | 1);
    output ^= output + Math.imul(output ^ (output >>> 7), output | 61);
    return ((output ^ (output >>> 14)) >>> 0) / 4_294_967_296;
  };
}

export function isWithinArizonaMockBounds({ latitude, longitude }) {
  return Number.isFinite(latitude)
    && Number.isFinite(longitude)
    && latitude >= ARIZONA_MOCK_BOUNDS.minimumLatitude
    && latitude <= ARIZONA_MOCK_BOUNDS.maximumLatitude
    && longitude >= ARIZONA_MOCK_BOUNDS.minimumLongitude
    && longitude <= ARIZONA_MOCK_BOUNDS.maximumLongitude;
}

function selectAnchor(value) {
  let remaining = value * ANCHOR_WEIGHT_TOTAL;
  for (const anchor of ARIZONA_REPORT_ANCHORS) {
    remaining -= anchor.weight;
    if (remaining < 0) return anchor;
  }
  return ARIZONA_REPORT_ANCHORS.at(-1);
}

function centeredJitter(random) {
  return random() + random() + random() - 1.5;
}

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}
